import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { forceLogout, refreshAccessToken } from './authSession';
import { getAccessToken } from '../utils/authTokens';

type RetriableConfig = InternalAxiosRequestConfig & { _authRetry?: boolean };

function isAuthSessionUrl(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  );
}

/**
 * Attach Bearer access token on requests, and on 401 try refresh once then retry.
 * Refresh failure (401) clears tokens and redirects to login.
 */
export function attachAuthInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetriableConfig | undefined;
      const status = error.response?.status;

      if (
        status !== 401 ||
        !original ||
        original._authRetry ||
        isAuthSessionUrl(original.url)
      ) {
        return Promise.reject(error);
      }

      original._authRetry = true;

      try {
        const accessToken = await refreshAccessToken();
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${accessToken}`;
        return client.request(original);
      } catch (refreshError) {
        // refreshAccessToken already forceLogout'd on 401 / missing token.
        // For other refresh failures, if we still have no access token, log out.
        if (!getAccessToken()) {
          forceLogout();
        }
        return Promise.reject(refreshError);
      }
    },
  );
}
